'use client'

// ---------------------------------------------------------------------------
// Library transport — ships the store's commands to the backend, in order.
//
// The store stays synchronous and optimistic (spec §5.2); this module owns
// everything network: a per-account, per-workspace durable FIFO of commands, a
// sequential shipper with backoff, the R2 upload flow, and the snapshot fetch.
// Serializable commands are mirrored to an authenticated-user-scoped
// localStorage key before shipping and removed only after acknowledgement (or
// an explicit 4xx drop), so a same-user hard reload can retry them safely.
// Upload-backed entries may be a Promise<command | null> while their media is
// moving to R2. Their optimistic command plus blob-key-only upload descriptors
// are durable immediately; after a same-user reload the store re-opens the
// IndexedDB blobs and the transport resumes the upload. The materialized https
// command replaces that descriptor only after every upload attempt settles.
// Oversized batches are flattened and shipped as ordered requests of at most
// 200 commands. The backend is intentionally non-transactional, so each acked
// prefix advances a durable cursor; snapshot reconciliation still replays the
// original optimistic gesture until its final fragment is acknowledged.
//
// idb: srcs NEVER reach the server: at ship time, upload-carrying commands
// are rewritten from the in-memory upload-results map (keyed by blobKey so
// undo/redo across replace-media re-ships the RIGHT pixels); commands whose
// upload fails after retry preparation are dropped (the server simply never
// learns about that asset). Interrupted uploads remain queued by blob pointer
// and are resumed after a same-owner reload.
//
// No store imports here (the store imports us): the store registers a bridge
// for the few callbacks the transport needs (local https swap after upload,
// drop-then-refetch, focus refetch).
// ---------------------------------------------------------------------------

import { authenticatedRequest } from '@/lib/authenticatedRequest'
import { getApiUrl } from '@/lib/serverConfig'
import { captureSentryError } from '@/lib/sentry'
import { getGhostWriteRequestHeaders } from '@/store/ghostModeStore'
import type {
  CommunityLibrarySnapshot,
  LibAsset,
  LibraryCommand,
  LibrarySnapshot,
  LibraryUploadFileInit,
  LibraryUploadsResponse,
} from '@/types/shared'

const CHUNK_SIZE = 50
// Mirror of backend validate.ts MAX_COMMANDS_PER_REQUEST: the server flattens
// batches and 400s past this, which drops the whole queue — so a POST must
// never combine entries beyond it.
const MAX_FLATTENED_PER_REQUEST = 200
const BACKOFF_BASE_MS = 1000
const BACKOFF_MAX_MS = 30_000

export type LibraryUploadResult = {
  src: string
  posterSrc?: string
  r2Key: string
  posterR2Key?: string
  width?: number
  height?: number
}

export type LibraryUploadRequest = {
  assetId: string
  file: File
  /** Which blob slot this upload fills (replace-media uses blob:<id>:rN). */
  blobKey?: string
  poster?: Blob | null
  /** Prepared locally with the blob; folded into the durable create/update. */
  width?: number
  height?: number
  /** create → failed upload removes the asset; replace → keeps the old media. */
  mode: 'create' | 'replace'
}

/** Serializable pointer to an upload body in the Library IndexedDB cache.
    Never contains bytes, object URLs, or data: URLs. The containing outbox is
    account-scoped; the transport only asks the store to open these keys after
    that same account has been rebound. */
export type LibraryDurableUpload = {
  assetId: string
  blobKey?: string
  fileName: string
  contentType: string
  sizeBytes: number
  lastModified?: number
  mode: 'create' | 'replace'
}

/**
 * Why the library is unavailable to this caller. Every value is a legitimate
 * server answer, not a failure:
 * - 'requires-account'    — an anonymous session; the library needs a real account.
 * - 'account-unavailable' — the account is deleted or its email domain is blocked.
 * - 'no-membership'       — a real account that isn't (or is no longer) a member.
 */
export type LibraryAccessDenialReason = 'requires-account' | 'account-unavailable' | 'no-membership'

export type LibraryStoreBridge = {
  /** Upload done: swap the local row to https (record:false, ship:false). */
  onUploadComplete: (
    workspaceId: string,
    assetId: string,
    patch: Partial<LibAsset>,
    expectedBlobKey: string | undefined,
    mode: 'create' | 'replace',
  ) => void
  /** Upload permanently failed. mode 'create' → remove the local asset. */
  onUploadFailed: (workspaceId: string, assetId: string, mode: 'create' | 'replace') => void
  /** Same-owner reload: reopen the request body from its IndexedDB blob key.
      The transport rechecks the auth epoch after this await and before I/O. */
  loadUploadRequest?: (upload: LibraryDurableUpload) => Promise<LibraryUploadRequest | null>
  /** Command ACKed: source/poster bytes are no longer needed for recovery. */
  cleanupUploadBlobs?: (uploads: LibraryDurableUpload[]) => Promise<void>
  /** The queue was dropped after a 4xx — refetch the snapshot (server wins). */
  onQueueDropped: (workspaceId: string) => void
  /** Auth owner changed: discard the previous account's visible store realm. */
  onAccountChanged: (accountId: string | null) => void
  /** The next account's durable queue is loaded; its intended workspace can now rehydrate. */
  onAccountReady?: (accountId: string) => void
  /** Access was refused — fail closed and surface a no-access state. */
  onAccessDenied: (workspaceId: string, reason: LibraryAccessDenialReason) => void
  /** A later successful snapshot proved membership was restored. */
  onAccessRestored: (workspaceId: string) => void
  /** Focus/online: refetch if nothing is pending. */
  refetchIfIdle: () => void
}

let bridge: LibraryStoreBridge | null = null
const uploadLoaderWaiters = new Set<() => void>()
export function registerLibraryStoreBridge(b: LibraryStoreBridge): void {
  bridge = b
  for (const wake of uploadLoaderWaiters) wake()
  uploadLoaderWaiters.clear()
  hydrateDurableQueues()
  if (activeAccountId) b.onAccountReady?.(activeAccountId)
  if (activeAccountId && testOverrides.autoShip !== false) {
    const epoch = accountEpoch
    for (const workspaceId of queues.keys()) void shipLoop(workspaceId, epoch)
  }
}

// --- Enablement -----------------------------------------------------------------

type TestOverrides = {
  enabled?: boolean
  post?: (workspaceId: string, commands: LibraryCommand[]) => Promise<PostOutcome>
  /** Keep commands queued for deterministic durability/FIFO tests. */
  autoShip?: boolean
  /** Collapse uploader retry delays in deterministic unit tests. */
  uploadRetryDelayMs?: number
}
const testOverrides: TestOverrides = {}
/** Test seam: force-enable with a mock poster (unit tests only). */
export function __setLibraryTransportTestOverrides(overrides: TestOverrides): void {
  Object.assign(testOverrides, overrides)
}

export function isLibraryTransportEnabled(): boolean {
  if (testOverrides.enabled !== undefined) return testOverrides.enabled
  if (typeof window === 'undefined') return false
  // The 24 store tests exercise the executor offline; never ship from tests.
  if (typeof process !== 'undefined' && process.env?.VITEST) return false
  return true
}

/** True only after auth has bound this transport to a concrete Supabase user. */
export function hasLibraryTransportAccount(): boolean {
  return activeAccountId !== null
}

// --- Queue state ------------------------------------------------------------------

type QueueEntry = LibraryCommand | Promise<LibraryCommand | null>
type PendingRows = { assetIds: Set<string>; collectionIds: Set<string>; projectIds: Set<string> }
type QueueItem = {
  id: string
  entry: QueueEntry
  /** Value-complete optimistic command, including while an upload is unresolved. */
  replayCommand?: LibraryCommand
  /** Number of flattened leaf commands already acknowledged by the server. */
  shippedLeaves: number
  rows: PendingRows
  /** Present only until this upload-backed entry settles. */
  uploads?: LibraryDurableUpload[]
  /** Same-realm fallback; never serialized. */
  runtimeUploads?: Map<string, LibraryUploadRequest>
  /** Retained durably after R2 succeeds, until the server command is ACKed. */
  cleanupUploads?: LibraryDurableUpload[]
  /** Server terminally rejected this queue; cleanup only, never POST again. */
  terminalDrop?: boolean
  /** Definite upload failures, notified only after replayCommand is repaired. */
  failedUploads?: LibraryDurableUpload[]
}

export type PendingLibraryCommand = { id: string; command: LibraryCommand }

type DurableQueueItem = {
  id: string
  command: LibraryCommand
  shippedLeaves?: number
  uploads?: LibraryDurableUpload[]
  cleanupUploads?: LibraryDurableUpload[]
  terminalDrop?: boolean
}
type DurableOutbox = { version: 2; accountId: string; workspaces: Record<string, DurableQueueItem[]> }

const LEGACY_OUTBOX_STORAGE_KEY = 'martini:library-command-outbox:v1'
const OUTBOX_STORAGE_PREFIX = 'martini:library-command-outbox:v2:'

class LibraryWorkspaceAccessDeniedError extends Error {}

const queues = new Map<string, QueueItem[]>()
const shipping = new Set<string>()
const accessDeniedWorkspaces = new Map<string, LibraryAccessDenialReason>()
const accessDenialVersions = new Map<string, number>()
const snapshotRequestVersions = new Map<string, number>()
const snapshotAccessProofVersions = new Map<string, number>()
/** blobKey-scoped upload results: undo/redo of replace-media re-ships correctly. */
const uploadResults = new Map<string, LibraryUploadResult>()
let activeAccountId: string | null = null
/** True while the bound session is a Supabase anonymous user, which every
    library route refuses by design (requireRealAccount). */
let activeAccountIsAnonymous = false
let accountEpoch = 0
let durableQueuesHydrated = false

/** Opaque ownership generation for async work that starts before it reaches
    the outbox (native-file normalization, poster capture, IndexedDB writes).
    `personal` is auth-relative, so workspaceId alone is never an ownership
    guard. */
export type LibraryTransportOwner = Readonly<{ accountId: string; epoch: number }>

export function captureLibraryTransportOwner(): LibraryTransportOwner | null {
  return activeAccountId ? { accountId: activeAccountId, epoch: accountEpoch } : null
}

export function isLibraryTransportOwnerCurrent(owner: LibraryTransportOwner | null): boolean {
  return !!owner && activeAccountId === owner.accountId && accountEpoch === owner.epoch
}

function waitForUploadLoader(): Promise<void> {
  if (bridge?.loadUploadRequest) return Promise.resolve()
  return new Promise(resolve => uploadLoaderWaiters.add(resolve))
}

function emptyPendingRows(): PendingRows {
  return { assetIds: new Set(), collectionIds: new Set(), projectIds: new Set() }
}

function queueItemId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function localStorageOrNull(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function outboxStorageKey(accountId: string): string {
  return `${OUTBOX_STORAGE_PREFIX}${accountId}`
}

function shippingKey(workspaceId: string, epoch = accountEpoch): string {
  return `${epoch}:${workspaceId}`
}

function persistDurableQueues(): void {
  const accountId = activeAccountId
  if (!accountId) return
  const storage = localStorageOrNull()
  if (!storage) return
  const workspaces: DurableOutbox['workspaces'] = {}
  for (const [workspaceId, queue] of queues) {
    const durable = queue.flatMap(item => {
      // Unresolved uploads are durable by pointer: the raw optimistic command
      // contains only idb:<assetId> markers and `uploads` contains only blob
      // keys + file metadata. The IndexedDB bytes never enter localStorage.
      if (item.uploads?.length && item.replayCommand) {
        return [
          {
            id: item.id,
            command: item.replayCommand,
            uploads: item.uploads,
            ...(item.cleanupUploads?.length ? { cleanupUploads: item.cleanupUploads } : {}),
            ...(item.terminalDrop ? { terminalDrop: true } : {}),
            ...(item.shippedLeaves ? { shippedLeaves: item.shippedLeaves } : {}),
          },
        ]
      }
      if (item.entry instanceof Promise) return []
      const command = rewriteForShip(item.entry)
      const durableCommand = command ?? (item.cleanupUploads?.length ? item.entry : null)
      return durableCommand
        ? [
            {
              id: item.id,
              command: durableCommand,
              ...(item.cleanupUploads?.length ? { cleanupUploads: item.cleanupUploads } : {}),
              ...(item.terminalDrop ? { terminalDrop: true } : {}),
              ...(item.shippedLeaves ? { shippedLeaves: item.shippedLeaves } : {}),
            },
          ]
        : []
    })
    if (durable.length) workspaces[workspaceId] = durable
  }
  try {
    if (Object.keys(workspaces).length) {
      storage.setItem(
        outboxStorageKey(accountId),
        JSON.stringify({ version: 2, accountId, workspaces } satisfies DurableOutbox),
      )
    } else {
      storage.removeItem(outboxStorageKey(accountId))
    }
  } catch (error) {
    captureSentryError(error, { operation: 'library_outbox_persist' })
  }
}

function isDurableUpload(value: unknown): value is LibraryDurableUpload {
  if (!value || typeof value !== 'object') return false
  const upload = value as Partial<LibraryDurableUpload>
  const validBlobKey =
    upload.blobKey === undefined ||
    (typeof upload.blobKey === 'string' &&
      typeof upload.assetId === 'string' &&
      (upload.blobKey === `blob:${upload.assetId}` || upload.blobKey.startsWith(`blob:${upload.assetId}:r`)))
  return (
    typeof upload.assetId === 'string' &&
    upload.assetId.length > 0 &&
    validBlobKey &&
    typeof upload.fileName === 'string' &&
    upload.fileName.length > 0 &&
    typeof upload.contentType === 'string' &&
    (upload.contentType.startsWith('image/') || upload.contentType.startsWith('video/')) &&
    typeof upload.sizeBytes === 'number' &&
    Number.isFinite(upload.sizeBytes) &&
    upload.sizeBytes > 0 &&
    (upload.lastModified === undefined || Number.isFinite(upload.lastModified)) &&
    (upload.mode === 'create' || upload.mode === 'replace')
  )
}

function hydrateDurableQueues(): void {
  const accountId = activeAccountId
  if (durableQueuesHydrated || !isLibraryTransportEnabled() || !accountId) return
  durableQueuesHydrated = true
  const storage = localStorageOrNull()
  if (!storage) return
  try {
    // v1 had no owner binding. In particular, its `personal` workspace alias
    // cannot be verified against the current account, so never migrate/replay it.
    storage.removeItem(LEGACY_OUTBOX_STORAGE_KEY)
    const encoded = storage.getItem(outboxStorageKey(accountId))
    if (!encoded) return
    const parsed = JSON.parse(encoded) as Partial<DurableOutbox>
    if (
      parsed.version !== 2 ||
      parsed.accountId !== accountId ||
      !parsed.workspaces ||
      typeof parsed.workspaces !== 'object'
    ) {
      storage.removeItem(outboxStorageKey(accountId))
      return
    }
    for (const [workspaceId, items] of Object.entries(parsed.workspaces)) {
      if (!Array.isArray(items)) continue
      const queue = queues.get(workspaceId) ?? []
      const knownIds = new Set(queue.map(item => item.id))
      for (const item of items) {
        if (!item || typeof item.id !== 'string' || !item.command || knownIds.has(item.id)) continue
        const shippedLeaves = item.shippedLeaves
        const restoredUploads =
          Array.isArray(item.uploads) && item.uploads.every(isDurableUpload) ? item.uploads : undefined
        const restoredCleanupUploads =
          Array.isArray(item.cleanupUploads) && item.cleanupUploads.every(isDurableUpload)
            ? item.cleanupUploads
            : undefined
        const terminalDrop = item.terminalDrop === true
        const uploads = terminalDrop ? undefined : restoredUploads
        const cleanupUploads = restoredCleanupUploads ?? (terminalDrop ? restoredUploads : undefined)
        const queueItem: QueueItem = {
          id: item.id,
          entry: item.command,
          replayCommand: item.command,
          shippedLeaves:
            typeof shippedLeaves === 'number' && Number.isInteger(shippedLeaves) && shippedLeaves > 0
              ? shippedLeaves
              : 0,
          rows: createdRows(item.command),
          uploads,
          cleanupUploads,
          terminalDrop,
        }
        queue.push(queueItem)
        if (uploads?.length) startUploadEntry(workspaceId, queueItem, accountId, accountEpoch)
        knownIds.add(item.id)
      }
      if (queue.length) queues.set(workspaceId, queue)
    }
  } catch (error) {
    storage.removeItem(outboxStorageKey(accountId))
    captureSentryError(error, { operation: 'library_outbox_restore' })
  }
}

/** Bind all durable queue reads/writes to the authenticated Supabase user.
    Changing accounts invalidates old in-memory shippers before hydrating the
    new user's independent outbox. Unscoped v1 data is deliberately discarded:
    `personal` cannot be mapped to an owner safely after the fact.

    `isAnonymous` is part of the identity here, not a detail of it: converting a
    guest keeps the SAME Supabase user id, so without it the early-return below
    would leave the guest's requires-account denial latched over the real
    account's library. Treating the change as an account transition reuses the
    existing invalidate-and-rehydrate path. */
export function setLibraryTransportAccount(accountId: string | null, options: { isAnonymous?: boolean } = {}): void {
  const nextAccountId = accountId?.trim() || null
  const nextIsAnonymous = nextAccountId !== null && options.isAnonymous === true
  const storage = localStorageOrNull()
  storage?.removeItem(LEGACY_OUTBOX_STORAGE_KEY)

  if (activeAccountId === nextAccountId && activeAccountIsAnonymous === nextIsAnonymous) {
    hydrateDurableQueues()
    if (nextAccountId) bridge?.onAccountReady?.(nextAccountId)
    if (bridge && nextAccountId && testOverrides.autoShip !== false) {
      const epoch = accountEpoch
      for (const workspaceId of queues.keys()) void shipLoop(workspaceId, epoch)
    }
    return
  }

  // Save the outgoing account's materialized work before invalidating its
  // in-memory realm. An already-in-flight POST may duplicate after a later
  // same-user login; backend command semantics make that safe.
  persistDurableQueues()
  accountEpoch++
  for (const wake of uploadLoaderWaiters) wake()
  uploadLoaderWaiters.clear()
  queues.clear()
  uploadResults.clear()
  durableQueuesHydrated = false
  activeAccountId = nextAccountId
  activeAccountIsAnonymous = nextIsAnonymous
  // The latch is workspace-keyed and `personal` is auth-relative, so a denial
  // proved for the outgoing identity says nothing about the incoming one.
  accessDeniedWorkspaces.clear()
  bridge?.onAccountChanged(nextAccountId)

  hydrateDurableQueues()
  if (nextAccountId) bridge?.onAccountReady?.(nextAccountId)
  if (bridge && nextAccountId && testOverrides.autoShip !== false) {
    const epoch = accountEpoch
    for (const workspaceId of queues.keys()) void shipLoop(workspaceId, epoch)
  }
}

function resultKey(assetId: string, blobKey?: string | null): string {
  return `${assetId}|${blobKey ?? `blob:${assetId}`}`
}

function durableUploadFromRequest(upload: LibraryUploadRequest): LibraryDurableUpload {
  return {
    assetId: upload.assetId,
    ...(upload.blobKey ? { blobKey: upload.blobKey } : {}),
    fileName: upload.file.name || 'file',
    contentType: upload.file.type,
    sizeBytes: upload.file.size,
    ...(Number.isFinite(upload.file.lastModified) ? { lastModified: upload.file.lastModified } : {}),
    mode: upload.mode,
  }
}

async function loadUploadRequest(
  descriptor: LibraryDurableUpload,
  fallback: LibraryUploadRequest | undefined,
  expectedAccountId: string,
  expectedEpoch: number,
): Promise<LibraryUploadRequest | null> {
  // In production the store bridge owns the IndexedDB schema and prepares a
  // poster/dimensions from the cached blob. A same-realm fallback keeps the
  // request usable if the bridge has not mounted yet; restored uploads wait
  // for the bridge rather than being discarded or sent past in FIFO order.
  for (;;) {
    if (activeAccountId !== expectedAccountId || accountEpoch !== expectedEpoch) return null
    const loader = bridge?.loadUploadRequest
    if (loader) {
      const loaded = await loader(descriptor)
      if (activeAccountId !== expectedAccountId || accountEpoch !== expectedEpoch) return null
      return loaded ?? fallback ?? null
    }
    if (fallback) return fallback
    await waitForUploadLoader()
  }
}

async function materializeUploadCommand(
  workspaceId: string,
  item: QueueItem,
  expectedAccountId: string,
  expectedEpoch: number,
): Promise<LibraryCommand | null> {
  const command = item.replayCommand
  if (!command) return null
  let accessDenied = false
  await Promise.all(
    (item.uploads ?? []).map(async descriptor => {
      const fallback = item.runtimeUploads?.get(resultKey(descriptor.assetId, descriptor.blobKey))
      let backoff = BACKOFF_BASE_MS
      let transientReported = false
      for (;;) {
        if (activeAccountId !== expectedAccountId || accountEpoch !== expectedEpoch) return
        try {
          const request = await loadUploadRequest(descriptor, fallback, expectedAccountId, expectedEpoch)
          if (!request) {
            if (activeAccountId !== expectedAccountId || accountEpoch !== expectedEpoch) return
            throw new LibraryUploadMissingBlobError()
          }
          await uploadLibraryMedia(workspaceId, request, expectedAccountId, expectedEpoch)
          return
        } catch (error) {
          // An account transition invalidates this realm; it is not an upload
          // failure and must not mutate or notify the next owner's store.
          if (activeAccountId !== expectedAccountId || accountEpoch !== expectedEpoch) return
          if (error instanceof LibraryWorkspaceAccessDeniedError) {
            accessDenied = true
            return
          }
          if (isTerminalLibraryUploadError(error)) {
            console.warn(`[library] upload failed for asset ${descriptor.assetId}:`, error)
            captureSentryError(error, {
              operation: 'library_upload',
              extra: { assetId: descriptor.assetId, mode: descriptor.mode },
            })
            item.failedUploads ??= []
            item.failedUploads.push(descriptor)
            return
          }

          // Network errors, 408/429, and 5xx responses retain the durable blob
          // descriptor and retry in FIFO order. Reporting only the first failure
          // avoids a Sentry event every 30 seconds while a client is offline.
          if (!transientReported) {
            transientReported = true
            captureSentryError(error, {
              operation: 'library_upload_retry',
              extra: { assetId: descriptor.assetId, mode: descriptor.mode },
            })
          }
          await sleep(testOverrides.uploadRetryDelayMs ?? backoff)
          backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
        }
      }
    }),
  )
  // Access denial invalidates the whole optimistic batch. Never preserve a
  // successful sibling or non-upload command if one upload was rejected.
  if (accessDenied) return null
  return activeAccountId === expectedAccountId && accountEpoch === expectedEpoch ? command : null
}

/** Pending snapshots captured by setWorkspace/refetch intentionally survive an
    ACK that races their GET. Keep that captured command object identity stable
    while replacing its value with the upload-materialized form; otherwise a
    blob-warmup race could later install its stale idb: version. */
function replaceReplayCommandValue(target: LibraryCommand, replacement: LibraryCommand): void {
  const mutable = target as unknown as Record<string, unknown>
  for (const key of Object.keys(mutable)) delete mutable[key]
  Object.assign(mutable, replacement)
}

function startUploadEntry(
  workspaceId: string,
  item: QueueItem,
  expectedAccountId: string,
  expectedEpoch: number,
): void {
  const cleanupUploads = item.uploads
  const entry = materializeUploadCommand(workspaceId, item, expectedAccountId, expectedEpoch)
  item.entry = entry
  void entry
    .then(command => {
      const queue = queues.get(workspaceId)
      if (!queue?.includes(item)) return
      item.uploads = undefined
      item.runtimeUploads = undefined
      item.cleanupUploads = cleanupUploads
      if (command) {
        item.entry = command
        const shippable = rewriteForShip(command)
        if (item.replayCommand) {
          replaceReplayCommandValue(
            item.replayCommand,
            shippable ?? { type: 'batch', label: 'Dropped library upload', commands: [] },
          )
        }
        item.replayCommand = shippable ? item.replayCommand : undefined
        item.rows = shippable ? createdRows(shippable) : emptyPendingRows()
      }
      persistDurableQueues()
      for (const failed of item.failedUploads ?? []) {
        bridge?.onUploadFailed(workspaceId, failed.assetId, failed.mode)
      }
      item.failedUploads = undefined
    })
    .catch(() => {})
}

export function isLibraryQueueBusy(workspaceId: string): boolean {
  hydrateDurableQueues()
  return (queues.get(workspaceId)?.length ?? 0) > 0 || shipping.has(shippingKey(workspaceId))
}

export function isLibraryWorkspaceAccessDenied(workspaceId: string): boolean {
  return accessDeniedWorkspaces.has(workspaceId)
}

/** The latched refusal for this workspace, or null while access holds. */
export function libraryWorkspaceDenialReason(workspaceId: string): LibraryAccessDenialReason | null {
  return accessDeniedWorkspaces.get(workspaceId) ?? null
}

function createdRows(command: LibraryCommand, rows: PendingRows = emptyPendingRows()) {
  if (command.type === 'asset.create') rows.assetIds.add(command.asset.id)
  if (command.type === 'collection.create') rows.collectionIds.add(command.collection.id)
  if (command.type === 'link.setProject') rows.projectIds.add(command.projectId)
  if (command.type === 'batch') command.commands.forEach(inner => createdRows(inner, rows))
  return rows
}

/** Optimistic rows/links that have not been acknowledged by the server yet. */
export function getPendingLibraryRows(workspaceId: string): PendingRows {
  hydrateDurableQueues()
  const rows = emptyPendingRows()
  for (const item of queues.get(workspaceId) ?? []) {
    if (item.terminalDrop) continue
    const pending = item.rows
    pending?.assetIds.forEach(id => rows.assetIds.add(id))
    pending?.collectionIds.forEach(id => rows.collectionIds.add(id))
    pending?.projectIds.forEach(id => rows.projectIds.add(id))
  }
  return rows
}

/** Value-complete pending commands in FIFO order. Replaying these over a fresh
    snapshot preserves every optimistic mutation, not only newly-created rows. */
export function getPendingLibraryCommands(workspaceId: string): PendingLibraryCommand[] {
  hydrateDurableQueues()
  return (queues.get(workspaceId) ?? []).flatMap(item => {
    if (item.terminalDrop) return []
    const command = item.replayCommand ?? (item.entry instanceof Promise ? undefined : item.entry)
    return command ? [{ id: item.id, command: rewriteAvailableForReplay(command) }] : []
  })
}

/** Enqueue a command (or a promise of one) for ordered shipping. */
export function enqueueLibraryCommand(
  workspaceId: string,
  entry: QueueEntry,
  rows?: PendingRows,
  replayCommand?: LibraryCommand,
): void {
  if (!isLibraryTransportEnabled() || isLibraryWorkspaceAccessDenied(workspaceId)) return
  if (!activeAccountId) {
    captureSentryError(new Error('Library command enqueued before auth account binding'), {
      operation: 'library_outbox_missing_account',
      extra: { workspaceId },
    })
    return
  }
  hydrateDurableQueues()
  const epoch = accountEpoch
  const q = queues.get(workspaceId) ?? []
  const item: QueueItem = {
    id: queueItemId(),
    entry,
    replayCommand: replayCommand ?? (entry instanceof Promise ? undefined : entry),
    shippedLeaves: 0,
    rows: rows ?? (entry instanceof Promise ? emptyPendingRows() : createdRows(entry)),
  }
  q.push(item)
  queues.set(workspaceId, q)
  persistDurableQueues()
  if (entry instanceof Promise) {
    // Collapse the entry to its materialized command once it settles, so the
    // pagehide keepalive flush (which cannot await) can still ship a command
    // whose upload finished moments before the tab died.
    void entry
      .then(cmd => {
        const queue = queues.get(workspaceId)
        if (!queue?.includes(item)) return
        if (cmd) {
          item.entry = cmd
          const shippable = rewriteForShip(cmd)
          item.replayCommand = shippable ?? undefined
          item.rows = shippable ? createdRows(shippable) : emptyPendingRows()
          persistDurableQueues()
        }
      })
      .catch(() => {})
  }
  if (testOverrides.autoShip !== false) void shipLoop(workspaceId, epoch)
}

/** Enqueue a command that must wait for its media uploads to finish first. */
export function enqueueLibraryCommandWithUploads(
  workspaceId: string,
  command: LibraryCommand,
  uploads: LibraryUploadRequest[],
  expectedOwner?: LibraryTransportOwner | null,
): boolean {
  if (!isLibraryTransportEnabled() || isLibraryWorkspaceAccessDenied(workspaceId)) return false
  if (expectedOwner && !isLibraryTransportOwnerCurrent(expectedOwner)) {
    captureSentryError(new Error('Library upload owner changed before enqueue'), {
      operation: 'library_outbox_owner_changed',
      extra: { workspaceId },
    })
    return false
  }
  const accountId = activeAccountId
  if (!accountId) {
    captureSentryError(new Error('Library upload enqueued before auth account binding'), {
      operation: 'library_outbox_missing_account',
      extra: { workspaceId },
    })
    return false
  }
  const epoch = accountEpoch
  hydrateDurableQueues()
  const q = queues.get(workspaceId) ?? []
  const descriptors = uploads.map(durableUploadFromRequest)
  const item: QueueItem = {
    id: queueItemId(),
    // Replaced by startUploadEntry synchronously below. Keeping the raw command
    // here lets persistDurableQueues write the resumable record first.
    entry: command,
    replayCommand: command,
    shippedLeaves: 0,
    rows: createdRows(command),
    uploads: descriptors,
    runtimeUploads: new Map(uploads.map(upload => [resultKey(upload.assetId, upload.blobKey), upload])),
  }
  q.push(item)
  queues.set(workspaceId, q)
  // Durability precedes poster capture, upload init, or any network await.
  persistDurableQueues()
  startUploadEntry(workspaceId, item, accountId, epoch)
  if (testOverrides.autoShip !== false) void shipLoop(workspaceId, epoch)
  return true
}

// --- Ship-time rewrite ---------------------------------------------------------------

/** Swap idb: srcs for their uploaded https URLs; null = not shippable (drop). */
export function rewriteForShip(cmd: LibraryCommand): LibraryCommand | null {
  switch (cmd.type) {
    case 'asset.create': {
      if (!cmd.asset.src.startsWith('idb:')) return cmd
      const result = uploadResults.get(resultKey(cmd.asset.id, cmd.asset.blobKey))
      if (!result) return null
      return {
        ...cmd,
        asset: {
          ...cmd.asset,
          src: result.src,
          posterSrc: result.posterSrc ?? cmd.asset.posterSrc,
          r2Key: result.r2Key,
          posterR2Key: result.posterR2Key,
          ...(result.width !== undefined ? { width: result.width } : {}),
          ...(result.height !== undefined ? { height: result.height } : {}),
          blobKey: undefined,
        },
      }
    }
    case 'asset.update': {
      if (!cmd.patch.src?.startsWith('idb:')) return cmd
      const result = uploadResults.get(resultKey(cmd.id, cmd.patch.blobKey))
      if (!result) return null
      return {
        ...cmd,
        patch: {
          ...cmd.patch,
          src: result.src,
          posterSrc: result.posterSrc ?? cmd.patch.posterSrc ?? null,
          r2Key: result.r2Key,
          posterR2Key: result.posterR2Key ?? cmd.patch.posterR2Key ?? null,
          ...(result.width !== undefined ? { width: result.width } : {}),
          ...(result.height !== undefined ? { height: result.height } : {}),
          // undefined keys drop out of the JSON body; the server's stale
          // blob_key is uploader-private and harmless.
          blobKey: undefined,
        },
      }
    }
    case 'batch': {
      const commands = cmd.commands.map(rewriteForShip).filter((c): c is LibraryCommand => c !== null)
      if (!commands.length) return null
      return { ...cmd, commands }
    }
    default:
      return cmd
  }
}

/** Snapshot replay keeps unresolved idb: children but eagerly substitutes any
    upload result already available. This closes the few-microtask window
    between uploadLibraryMedia publishing its result and the queue promise
    collapsing to its final command. */
function rewriteAvailableForReplay(command: LibraryCommand): LibraryCommand {
  if (command.type === 'batch') {
    return { ...command, commands: command.commands.map(rewriteAvailableForReplay) }
  }
  return rewriteForShip(command) ?? command
}

/** The backend flattens batch wrappers before applying them. Flatten here too
    so every request can honor its hard 200-command validation boundary while
    preserving the gesture's exact leaf-command order. */
function flattenForShip(command: LibraryCommand): LibraryCommand[] {
  return command.type === 'batch' ? command.commands.flatMap(flattenForShip) : [command]
}

// --- The shipper ----------------------------------------------------------------------

/** The object form carries WHY the server refused, so the store can tell a
    guest to sign up rather than to switch workspaces. */
type PostOutcome = 'ok' | 'drop' | 'retry' | { denied: LibraryAccessDenialReason }

/** Membership 403s from resolveWorkspace — identified by message, since that
    layer answers without an error code. */
const WORKSPACE_ACCESS_DENIED_ERRORS = new Set([
  'Not a member of this workspace',
  'Not authorized to use this workspace library',
])

/** Coded 403s from the requireRealAccount middleware, which fronts every
    library route. 'requires-account' is the anonymous visitor — by far the
    largest population, since a signed-out visit bootstraps an anonymous
    session and the panel hydrates on canvas mount.

    A Map, not an object literal: the lookup key comes off the wire, and an
    object would also answer to every `Object.prototype` name — a body with
    `code: 'toString'` would classify as a function. */
const ACCOUNT_DENIAL_REASONS = new Map<string, LibraryAccessDenialReason>([
  ['REQUIRES_REAL_ACCOUNT', 'requires-account'],
  ['ACCOUNT_DELETED', 'account-unavailable'],
  ['EMAIL_DOMAIN_BLOCKED', 'account-unavailable'],
  ['EMAIL_ALREADY_EXISTS', 'account-unavailable'],
])

/**
 * Why the server refused this workspace. The distinction is what the UI shows:
 * a guest needs a sign-up prompt, a former member needs a workspace switcher.
 * An unrecognized 403 deliberately classifies as null so it keeps throwing —
 * silencing the unknown is how a real authz defect goes unnoticed.
 */
function classifyLibraryDenial(status: number, body: string): LibraryAccessDenialReason | null {
  if (status !== 403) return null
  try {
    const { error, code } = JSON.parse(body) as { error?: unknown; code?: unknown }
    const coded = typeof code === 'string' ? ACCOUNT_DENIAL_REASONS.get(code) : undefined
    if (coded) return coded
    if (typeof error === 'string' && WORKSPACE_ACCESS_DENIED_ERRORS.has(error)) return 'no-membership'
    return null
  } catch {
    return null
  }
}

function accessDenialVersion(workspaceId: string): number {
  return accessDenialVersions.get(workspaceId) ?? 0
}

function beginSnapshotRequest(workspaceId: string): number {
  const version = (snapshotRequestVersions.get(workspaceId) ?? 0) + 1
  snapshotRequestVersions.set(workspaceId, version)
  return version
}

function isLatestSnapshotRequest(workspaceId: string, version: number): boolean {
  return snapshotRequestVersions.get(workspaceId) === version
}

function clearWorkspaceQueue(workspaceId: string): void {
  queues.set(workspaceId, [])
  // The durable outbox must forget them too, or a reload would replay commands
  // the server has already refused.
  persistDurableQueues()
}

function denyWorkspaceAccess(workspaceId: string, reason: LibraryAccessDenialReason): void {
  // The queue holds the ONLY descriptors for staged IndexedDB blobs (source +
  // poster, possibly hundreds of MB). Collect them before clearing or the
  // files become permanently unreachable and eat browser storage.
  const orphanedUploads = (queues.get(workspaceId) ?? []).flatMap(item => item.cleanupUploads ?? item.uploads ?? [])
  clearWorkspaceQueue(workspaceId)
  if (orphanedUploads.length && activeAccountId) {
    retryUploadBlobCleanupInBackground(workspaceId, orphanedUploads, activeAccountId, accountEpoch)
  }
  // Re-latching the SAME refusal must stay silent — the queue is already
  // cleared and re-notifying would re-toast on every focus refetch. A DIFFERENT
  // refusal is new information (a member whose account was since deleted would
  // otherwise keep being told to switch workspaces), so it re-notifies.
  if (accessDeniedWorkspaces.get(workspaceId) === reason) return
  accessDeniedWorkspaces.set(workspaceId, reason)
  // Bump on the change too: an in-flight GET that predates this refusal must
  // not restore access when it lands (fail closed).
  accessDenialVersions.set(workspaceId, accessDenialVersion(workspaceId) + 1)
  bridge?.onAccessDenied(workspaceId, reason)
}

async function throwUploadResponseError(
  response: Response,
  workspaceId: string,
  stage: 'init' | 'direct',
): Promise<never> {
  // Only a 403 can name a membership loss, and the body is the only way to tell
  // it apart from an ordinary forbidden. Every other status keeps the upload
  // error taxonomy that decides terminal-vs-retry.
  if (response.status === 403) {
    let body = ''
    try {
      body = await response.text()
    } catch {
      body = ''
    }
    const reason = classifyLibraryDenial(response.status, body)
    if (reason) {
      denyWorkspaceAccess(workspaceId, reason)
      throw new LibraryWorkspaceAccessDeniedError('Library workspace access denied')
    }
  }
  throw new LibraryUploadHttpError(stage, response.status)
}

function restoreWorkspaceAccess(workspaceId: string): void {
  if (!accessDeniedWorkspaces.delete(workspaceId)) return
  bridge?.onAccessRestored(workspaceId)
}

/** Test seam: exercise the registered store bridge without a real request. */
export function __setLibraryWorkspaceAccessForTest(
  workspaceId: string,
  denied: boolean,
  reason: LibraryAccessDenialReason = 'no-membership',
): void {
  if (denied) denyWorkspaceAccess(workspaceId, reason)
  else restoreWorkspaceAccess(workspaceId)
}

async function postCommands(
  workspaceId: string,
  commands: LibraryCommand[],
  expectedAccountId: string,
): Promise<PostOutcome> {
  if (testOverrides.post) return testOverrides.post(workspaceId, commands)
  try {
    const response = await authenticatedRequest(
      `${getApiUrl()}/library/${workspaceId}/commands`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getGhostWriteRequestHeaders(workspaceId) },
        body: JSON.stringify({ commands }),
      },
      { expectedUserId: expectedAccountId },
    )
    if (response.ok) return 'ok'
    if (response.status >= 400 && response.status < 500) {
      const body = await response.text().catch(() => '')
      const reason = classifyLibraryDenial(response.status, body)
      if (reason) return { denied: reason }
      captureSentryError(new Error(`library commands rejected (${response.status}): ${body.slice(0, 300)}`), {
        operation: 'library_commands_rejected',
        extra: { workspaceId, status: response.status, count: commands.length },
      })
      return 'drop'
    }
    return 'retry'
  } catch {
    return 'retry' // network — queue stays intact
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function retryUploadBlobCleanupInBackground(
  workspaceId: string,
  uploads: LibraryDurableUpload[],
  expectedAccountId: string,
  expectedEpoch: number,
): void {
  void (async () => {
    let backoff = BACKOFF_BASE_MS
    // Best effort only: command delivery must not depend on local cache
    // housekeeping. A future reload cannot safely replay an ACKed command just
    // to retain an unbounded cleanup job, so retry a few times in this realm.
    for (let attempt = 0; attempt < 3; attempt++) {
      await sleep(testOverrides.uploadRetryDelayMs ?? backoff)
      if (activeAccountId !== expectedAccountId || accountEpoch !== expectedEpoch) return
      const cleanup = bridge?.cleanupUploadBlobs
      if (!cleanup) return
      try {
        await cleanup(uploads)
        return
      } catch (error) {
        if (attempt === 2) {
          captureSentryError(error, {
            operation: 'library_upload_blob_cleanup_abandoned',
            extra: { workspaceId, count: uploads.length },
          })
        }
        backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
      }
    }
  })()
}

async function cleanupAcknowledgedUploads(workspaceId: string, item: QueueItem): Promise<boolean> {
  const uploads = item.cleanupUploads
  if (!uploads?.length) return true
  // Older tests/embedders may register a bridge without blob cleanup. The
  // production Library store always supplies it; absence must not deadlock the
  // command FIFO.
  if (!bridge?.cleanupUploadBlobs) return true
  try {
    await bridge.cleanupUploadBlobs(uploads)
    item.cleanupUploads = undefined
    return true
  } catch (error) {
    captureSentryError(error, {
      operation: 'library_upload_blob_cleanup',
      extra: { workspaceId, count: uploads.length },
    })
    // The server cursor was persisted before this cleanup attempt. Remove the
    // recovery descriptor and let the FIFO advance; retry deletion separately
    // so this already-ACKed command is never POSTed again and later edits cannot
    // be held hostage by a broken IndexedDB delete transaction.
    item.cleanupUploads = undefined
    if (activeAccountId) {
      retryUploadBlobCleanupInBackground(workspaceId, uploads, activeAccountId, accountEpoch)
    }
    return true
  }
}

async function shipLoop(workspaceId: string, epoch = accountEpoch): Promise<void> {
  const accountId = activeAccountId
  if (!accountId || epoch !== accountEpoch) return
  const key = shippingKey(workspaceId, epoch)
  if (shipping.has(key)) return
  shipping.add(key)
  let backoff = BACKOFF_BASE_MS
  // After a multi-item 409, the server stopped at the first failing command —
  // items later in that POST were never applied, so dropping them all would
  // lose real edits the refetch cannot restore. Isolation mode re-ships one
  // item per POST until one succeeds; only the item that actually conflicts
  // drops. Idempotent command semantics make re-sending the applied prefix
  // safe (a re-applied CAS loss just 409s alone and drops alone).
  let isolateConflicts = false
  try {
    for (;;) {
      if (!activeAccountId || epoch !== accountEpoch) return
      const q = queues.get(workspaceId) ?? []
      if (!q.length) return

      if (q[0].terminalDrop) {
        const dropped = q[0]
        if (dropped.entry instanceof Promise) await dropped.entry.catch(() => null)
        if (!activeAccountId || epoch !== accountEpoch) return
        if (!(await cleanupAcknowledgedUploads(workspaceId, dropped))) {
          await sleep(backoff)
          backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
          continue
        }
        if (!activeAccountId || epoch !== accountEpoch) return
        q.shift()
        persistDurableQueues()
        backoff = BACKOFF_BASE_MS
        continue
      }

      // Materialize an ordered prefix. Awaiting a promise here IS the upload
      // ordering guarantee. Batch wrappers have no server-side transaction
      // semantics (validation flattens them), so send leaf commands directly
      // and advance each queue item's durable cursor only after that request's
      // ack. The original replayCommand stays intact until every leaf is done.
      const chunk: LibraryCommand[] = []
      const advances: Array<{ item: QueueItem; leafCount: number; leafTotal: number }> = []
      let inspected = 0
      let includedItems = 0
      const itemCap = isolateConflicts ? 1 : CHUNK_SIZE
      while (inspected < q.length && includedItems < itemCap && chunk.length < MAX_FLATTENED_PER_REQUEST) {
        const item = q[inspected]
        const entry = item.entry
        const cmd = entry instanceof Promise ? await entry.catch(() => null) : entry
        if (!activeAccountId || epoch !== accountEpoch) return
        if (!cmd) {
          // Do not discard a later null entry before an earlier POST is acked.
          if (chunk.length) break
          if (!(await cleanupAcknowledgedUploads(workspaceId, item))) {
            await sleep(backoff)
            backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
            continue
          }
          if (!activeAccountId || epoch !== accountEpoch) return
          q.splice(inspected, 1)
          persistDurableQueues()
          continue
        }
        const shippable = rewriteForShip(cmd)
        if (!shippable) {
          if (chunk.length) break
          if (!(await cleanupAcknowledgedUploads(workspaceId, item))) {
            await sleep(backoff)
            backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
            continue
          }
          if (!activeAccountId || epoch !== accountEpoch) return
          q.splice(inspected, 1)
          persistDurableQueues()
          continue
        }
        const leaves = flattenForShip(shippable)
        const remaining = leaves.slice(item.shippedLeaves)
        if (!remaining.length) {
          if (chunk.length) break
          if (!(await cleanupAcknowledgedUploads(workspaceId, item))) {
            await sleep(backoff)
            backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
            continue
          }
          if (!activeAccountId || epoch !== accountEpoch) return
          q.splice(inspected, 1)
          persistDurableQueues()
          continue
        }
        const capacity = MAX_FLATTENED_PER_REQUEST - chunk.length
        const nextLeaves = remaining.slice(0, capacity)
        chunk.push(...nextLeaves)
        advances.push({ item, leafCount: nextLeaves.length, leafTotal: leaves.length })
        includedItems++
        if (nextLeaves.length < remaining.length) break
        inspected++
      }

      if (!chunk.length) continue

      if (!activeAccountId || epoch !== accountEpoch) return
      const outcome = await postCommands(workspaceId, chunk, accountId)
      if (!activeAccountId || epoch !== accountEpoch) return
      if (outcome === 'ok') {
        isolateConflicts = false
        const completed = new Set<QueueItem>()
        for (const advance of advances) {
          advance.item.shippedLeaves += advance.leafCount
          if (advance.item.shippedLeaves >= advance.leafTotal) completed.add(advance.item)
        }
        // Persist the full ACK cursor before deleting local recovery bytes. If
        // cleanup or the tab dies, a reload retries cleanup without re-POSTing.
        persistDurableQueues()
        let cleanupFailed = false
        while (q[0] && completed.has(q[0])) {
          if (!(await cleanupAcknowledgedUploads(workspaceId, q[0]))) {
            cleanupFailed = true
            break
          }
          if (!activeAccountId || epoch !== accountEpoch) return
          q.shift()
        }
        persistDurableQueues()
        if (cleanupFailed) {
          await sleep(backoff)
          backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
          continue
        }
        backoff = BACKOFF_BASE_MS
        continue
      }
      if (outcome === 'drop') {
        if (advances.length > 1) {
          // The server stops at the first failing command, so this multi-item
          // POST contains items that never applied. Don't condemn them —
          // switch to one-item POSTs so only the conflicting item drops.
          isolateConflicts = true
          continue
        }
        // Single item refused: its optimistic state is a lie. Items behind it
        // were never sent; they re-ship after the refetch and succeed or drop
        // on their own merits (a queued write to an unrelated collection must
        // not die for another command's conflict).
        for (const advance of advances) {
          advance.item.terminalDrop = true
          advance.item.cleanupUploads ??= advance.item.uploads
        }
        persistDurableQueues()
        bridge?.onQueueDropped(workspaceId)
        continue
      }
      if (typeof outcome === 'object') {
        denyWorkspaceAccess(workspaceId, outcome.denied)
        return
      }
      await sleep(backoff)
      backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
    }
  } finally {
    shipping.delete(key)
    // Work enqueued while we were waiting on the last POST.
    if (
      activeAccountId &&
      epoch === accountEpoch &&
      testOverrides.autoShip !== false &&
      (queues.get(workspaceId)?.length ?? 0) > 0
    ) {
      void shipLoop(workspaceId, epoch)
    }
  }
}

// --- Uploads ----------------------------------------------------------------------------

class LibraryUploadMissingBlobError extends Error {
  constructor() {
    super('Library upload blob is no longer available')
    this.name = 'LibraryUploadMissingBlobError'
  }
}

class LibraryUploadHttpError extends Error {
  constructor(
    readonly stage: 'init' | 'put' | 'direct',
    readonly status: number,
  ) {
    super(`Library upload ${stage} failed (${status})`)
    this.name = 'LibraryUploadHttpError'
  }
}

function isTerminalLibraryUploadError(error: unknown): boolean {
  if (error instanceof LibraryUploadMissingBlobError) return true
  if (!(error instanceof LibraryUploadHttpError)) return false
  // Timeouts/rate limits and ambiguous no-overwrite conflicts are safe to
  // retry through a fresh upload init; all other client responses are definite.
  return error.status >= 400 && error.status < 500 && ![408, 409, 412, 429].includes(error.status)
}

async function putToUrl(uploadUrl: string, body: Blob, contentType: string): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType, 'If-None-Match': '*' },
    body,
  })
  if (!response.ok) throw new LibraryUploadHttpError('put', response.status)
}

async function putDirect(
  workspaceId: string,
  key: string,
  token: string,
  body: Blob,
  expectedAccountId: string,
): Promise<void> {
  const form = new FormData()
  form.set('key', key)
  form.set('token', token)
  form.set('file', body)
  const response = await authenticatedRequest(
    `${getApiUrl()}/library/${workspaceId}/uploads/direct`,
    {
      method: 'POST',
      headers: getGhostWriteRequestHeaders(workspaceId),
      body: form,
    },
    { expectedUserId: expectedAccountId },
  )
  if (!response.ok) await throwUploadResponseError(response, workspaceId, 'direct')
}

async function uploadLibraryMedia(
  workspaceId: string,
  u: LibraryUploadRequest,
  expectedAccountId: string,
  expectedEpoch: number,
): Promise<LibraryUploadResult> {
  const assertOwner = () => {
    if (activeAccountId !== expectedAccountId || accountEpoch !== expectedEpoch) {
      throw new Error('Library upload owner changed before completion')
    }
  }
  assertOwner()
  const initResponse = await authenticatedRequest(
    `${getApiUrl()}/library/${workspaceId}/uploads`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getGhostWriteRequestHeaders(workspaceId) },
      body: JSON.stringify({
        files: [
          {
            assetId: u.assetId,
            filename: u.file.name || 'file',
            contentType: u.file.type,
            sizeBytes: u.file.size,
            ...(u.poster ? { poster: { contentType: 'image/jpeg', sizeBytes: u.poster.size } } : {}),
          },
        ],
      }),
    },
    { expectedUserId: expectedAccountId },
  )
  if (!initResponse.ok) await throwUploadResponseError(initResponse, workspaceId, 'init')
  const { files } = (await initResponse.json()) as LibraryUploadsResponse
  const init: LibraryUploadFileInit | undefined = files[0]
  if (!init) throw new Error('upload init returned no file')

  assertOwner()
  if (init.uploadUrl) await putToUrl(init.uploadUrl, u.file, u.file.type)
  else {
    if (!init.directUploadToken) throw new Error('upload init returned no direct upload token')
    await putDirect(workspaceId, init.key, init.directUploadToken, u.file, expectedAccountId)
  }

  if (u.poster && init.poster) {
    assertOwner()
    if (init.poster.uploadUrl) await putToUrl(init.poster.uploadUrl, u.poster, 'image/jpeg')
    else {
      if (!init.poster.directUploadToken) throw new Error('upload init returned no direct poster upload token')
      await putDirect(workspaceId, init.poster.key, init.poster.directUploadToken, u.poster, expectedAccountId)
    }
  }

  assertOwner()

  const result: LibraryUploadResult = {
    src: init.publicUrl,
    posterSrc: u.poster && init.poster ? init.poster.publicUrl : undefined,
    r2Key: init.key,
    posterR2Key: u.poster && init.poster ? init.poster.key : undefined,
    width: u.width,
    height: u.height,
  }
  uploadResults.set(resultKey(u.assetId, u.blobKey), result)

  // Swap the local row to https so the @mention idb: filter lifts and other
  // devices see the same URLs after their next snapshot.
  bridge?.onUploadComplete(
    workspaceId,
    u.assetId,
    {
      src: result.src,
      ...(result.posterSrc ? { posterSrc: result.posterSrc } : {}),
      r2Key: result.r2Key,
      ...(result.posterR2Key ? { posterR2Key: result.posterR2Key } : {}),
      ...(result.width !== undefined ? { width: result.width } : {}),
      ...(result.height !== undefined ? { height: result.height } : {}),
      blobKey: undefined,
    },
    u.blobKey,
    u.mode,
  )
  return result
}

/** Test seam: seed an upload result (unit tests only). */
export function __setLibraryUploadResultForTest(
  assetId: string,
  blobKey: string | undefined,
  result: LibraryUploadResult,
): void {
  uploadResults.set(resultKey(assetId, blobKey), result)
}

// --- Snapshot ---------------------------------------------------------------------------

/** 'unconfigured' (404 / transport off) falls back to the bundled examples;
    'error' is an operational failure the UI must surface — in prod the
    fallback is empty, so collapsing errors would read as "nothing published". */
export type CommunityLibraryFetch =
  { status: 'ok'; snapshot: CommunityLibrarySnapshot } | { status: 'unconfigured' } | { status: 'error' }

/** The published community library — a scrubbed live snapshot of the source workspace. */
export async function fetchCommunityLibrarySnapshot(): Promise<CommunityLibraryFetch> {
  const accountId = activeAccountId
  const epoch = accountEpoch
  if (!isLibraryTransportEnabled() || !accountId) return { status: 'unconfigured' }
  try {
    const response = await authenticatedRequest(
      `${getApiUrl()}/library/community/snapshot`,
      {},
      { expectedUserId: accountId },
    )
    if (activeAccountId !== accountId || accountEpoch !== epoch) return { status: 'error' }
    if (response.status === 404) return { status: 'unconfigured' }
    if (!response.ok) throw new Error(`community library fetch failed (${response.status})`)
    return { status: 'ok', snapshot: (await response.json()) as CommunityLibrarySnapshot }
  } catch (error) {
    console.error('[library] community library fetch failed:', error)
    captureSentryError(error, { operation: 'library_community_fetch' })
    return { status: 'error' }
  }
}

/**
 * Project-scoped snapshot for users with project access but no org membership
 * (link guests). Deliberately outside the workspace denial latch: the vault
 * being denied is exactly the situation this read grant exists for.
 */
export async function fetchProjectScopedLibrarySnapshot(
  workspaceId: string,
  projectId: string,
): Promise<LibrarySnapshot | null> {
  const accountId = activeAccountId
  if (!isLibraryTransportEnabled() || !accountId) return null
  // Behind requireRealAccount too, so an anonymous viewer is refused before the
  // project grant is ever consulted. Return the same null the 403 produced —
  // never latch here, or a guest link would fail the vault closed as well.
  if (activeAccountIsAnonymous) return null
  try {
    const response = await authenticatedRequest(
      `${getApiUrl()}/library/${workspaceId}/project/${projectId}`,
      {},
      { expectedUserId: accountId },
    )
    if (activeAccountId !== accountId) return null
    if (!response.ok) return null
    return (await response.json()) as LibrarySnapshot
  } catch {
    return null
  }
}

export async function fetchLibrarySnapshot(workspaceId: string): Promise<LibrarySnapshot | null> {
  const accountId = activeAccountId
  const epoch = accountEpoch
  if (!isLibraryTransportEnabled() || !accountId) return null
  // An anonymous session cannot have a library: every library route sits behind
  // requireRealAccount, so this GET is answered 403 by definition. Latch the
  // denial locally instead of asking — a signed-out visitor mounts the canvas
  // (and refetches on focus) like anyone else, so the round trip is pure noise.
  // Signing up rebinds the transport, which clears this latch and rehydrates.
  if (activeAccountIsAnonymous) {
    denyWorkspaceAccess(workspaceId, 'requires-account')
    return null
  }
  const requestVersion = beginSnapshotRequest(workspaceId)
  const denialVersionAtStart = accessDenialVersion(workspaceId)
  try {
    const response = await authenticatedRequest(
      `${getApiUrl()}/library/${workspaceId}`,
      {},
      { expectedUserId: accountId },
    )
    if (activeAccountId !== accountId || accountEpoch !== epoch) return null
    if (response.status === 403) {
      const body = await response.text().catch(() => '')
      const reason = classifyLibraryDenial(response.status, body)
      if (reason) {
        // Focus/online can overlap. An older denied response must not relatch
        // access after a newer successful snapshot proved access. Merely
        // starting a newer request is not enough to discard a real denial.
        if ((snapshotAccessProofVersions.get(workspaceId) ?? 0) > requestVersion) return null
        denyWorkspaceAccess(workspaceId, reason)
        return null
      }
      throw new Error(`library snapshot failed (${response.status})`)
    }
    if (!response.ok) throw new Error(`library snapshot failed (${response.status})`)
    const snapshot = (await response.json()) as LibrarySnapshot
    if (!isLatestSnapshotRequest(workspaceId, requestVersion)) return null
    // A command may have proved access was revoked while this GET was in
    // flight. Never let its older successful response clear that newer denial.
    if (accessDenialVersion(workspaceId) !== denialVersionAtStart) return null
    snapshotAccessProofVersions.set(workspaceId, requestVersion)
    restoreWorkspaceAccess(workspaceId)
    return snapshot
  } catch (error) {
    console.error('[library] snapshot fetch failed:', error)
    captureSentryError(error, { operation: 'library_snapshot_fetch', extra: { workspaceId } })
    return null
  }
}

// --- Window wiring (refetch on focus/online; best-effort flush on pagehide) --------------

/** A pagehide flush may only send a materialized prefix. Skipping an unresolved
    upload and sending a later command would violate the same FIFO guarantee as
    the normal shipper (for example, a project link could overtake its create). */
function pagehideCommandPrefix(workspaceId: string): LibraryCommand[] {
  hydrateDurableQueues()
  const ready: LibraryCommand[] = []
  for (const item of queues.get(workspaceId) ?? []) {
    if (item.entry instanceof Promise) break
    const command = rewriteForShip(item.entry)
    if (!command) break
    const remaining = flattenForShip(command).slice(item.shippedLeaves)
    if (!remaining.length) continue
    const capacity = MAX_FLATTENED_PER_REQUEST - ready.length
    ready.push(...remaining.slice(0, capacity))
    if (remaining.length > capacity || ready.length === MAX_FLATTENED_PER_REQUEST) break
  }
  return ready
}

/** Test seam for the pagehide ordering invariant. */
export function __getLibraryPagehideCommandsForTest(workspaceId: string): LibraryCommand[] {
  return pagehideCommandPrefix(workspaceId)
}

/** Test seam that simulates a fresh JS realm while optionally retaining the
    durable localStorage outbox. Call only with autoShip:false / idle queues. */
export function __resetLibraryTransportMemoryForTest(options: { preserveDurable?: boolean } = {}): void {
  const accountId = activeAccountId
  const storage = localStorageOrNull()
  if (!options.preserveDurable && accountId) storage?.removeItem(outboxStorageKey(accountId))
  if (!options.preserveDurable) storage?.removeItem(LEGACY_OUTBOX_STORAGE_KEY)
  accountEpoch++
  for (const wake of uploadLoaderWaiters) wake()
  uploadLoaderWaiters.clear()
  activeAccountId = null
  activeAccountIsAnonymous = false
  queues.clear()
  shipping.clear()
  uploadResults.clear()
  durableQueuesHydrated = false
}

if (typeof window !== 'undefined' && isLibraryTransportEnabled()) {
  const refetch = () => bridge?.refetchIfIdle()
  window.addEventListener('focus', refetch)
  window.addEventListener('online', refetch)
  window.addEventListener('pagehide', () => {
    // The durable copy remains until the normal shipper receives an ack, so a
    // reload safely retries if this best-effort keepalive response is lost.
    for (const workspaceId of queues.keys()) {
      if (isLibraryWorkspaceAccessDenied(workspaceId)) continue
      // A shipper POST may already carry this prefix; sending it twice makes
      // CAS commands lose to themselves (409 → spurious drop). The durable
      // copy persists until ack, so a reload retries anything the in-flight
      // request loses.
      if (shipping.has(shippingKey(workspaceId))) continue
      const ready = pagehideCommandPrefix(workspaceId)
      if (!ready.length) continue
      const accountId = activeAccountId
      if (!accountId) continue
      void authenticatedRequest(
        `${getApiUrl()}/library/${workspaceId}/commands`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getGhostWriteRequestHeaders(workspaceId) },
          body: JSON.stringify({ commands: ready }),
          keepalive: true,
        },
        { expectedUserId: accountId },
      ).catch(() => {})
    }
  })
}
